import {
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import EmptyState from "components/empty-state";
import {
    BookedEverythingMessage,
    ExperienceGrid,
    SectionList,
    WebRuleList,
} from "components/pages/experience-view/experience-view-parts";
import { ExperienceViewContentSkeleton } from "components/pages/experience-view/experience-view-skeleton";
import type { ExperienceViewState } from "components/pages/experience-view/experience-view-utils";
import { NudgeHeader } from "components/selection-components/nudge-header";
import { useTranslation } from "react-i18next";
import type { AvailableTimesSection } from "types/available-times";
import type {
    ExperienceViewExperienceItem,
    ExperienceViewSectionItem,
} from "types/experience-view";

interface SimpleBookingViewProps {
    state: ExperienceViewState;
    onExperienceClick: (experience: ExperienceViewExperienceItem) => void;
    onWebRuleClick: (webRuleAsSection: AvailableTimesSection) => void;
}

/**
 * Experience view content for non-activity bookings (simple booking mode).
 * Shows experiences + webrules; falls back to empty states when none apply.
 */
function SimpleBookingView({ state, onExperienceClick, onWebRuleClick }: SimpleBookingViewProps) {
    const { t } = useTranslation();
    const {
        experiences,
        experiencesNotBookedYet,
        experienceLayouts,
        experienceAvailability,
        sections,
        sectionsNotBookedYet,
        webRules,
        hasBookedItems,
        hasDateSelected,
    } = state;

    if (experiences.length === 0 && webRules.length === 0 && !hasBookedItems) {
        if (!hasDateSelected) return null;
        return (
            <EmptyState
                header={t("experienceView.tabs.nothingAvailable")}
                description={t("experienceView.tabs.emptyExperiences")}
            />
        );
    }

    return (
        <Stack spacing={ThemeSpaceVariable.None}>
            {hasBookedItems && experiencesNotBookedYet.length > 0 && <NudgeHeader fullWidth />}
            <ExperienceGrid
                experiences={experiencesNotBookedYet}
                layouts={experienceLayouts}
                availability={experienceAvailability}
                onExperienceClick={onExperienceClick}
            />
            <WebRuleList
                webRules={webRules}
                sections={sections}
                divider={experiencesNotBookedYet.length > 0}
                onWebRuleClick={onWebRuleClick}
            />
            <BookedEverythingMessage
                isActivityBooking={false}
                sectionsNotBookedYet={sectionsNotBookedYet}
                experiencesNotBookedYet={experiencesNotBookedYet}
                hasBookedItems={hasBookedItems}
                webRules={webRules}
            />
        </Stack>
    );
}

interface ActivitySingleViewProps {
    state: ExperienceViewState;
    onExperienceClick: (experience: ExperienceViewExperienceItem) => void;
    onSectionClick: (section: ExperienceViewSectionItem) => void;
}

/**
 * Experience view content for activity bookings when only one content type
 * (experiences OR sections) is available — no tabs, renders the combined list.
 */
function ActivitySingleView({ state, onExperienceClick, onSectionClick }: ActivitySingleViewProps) {
    const { t } = useTranslation();
    const {
        experiences,
        experiencesNotBookedYet,
        experienceLayouts,
        experienceAvailability,
        sectionAvailability,
        sections,
        sectionsNotBookedYet,
        hasBookedItems,
    } = state;

    if (sections.length === 0 && experiences.length === 0) {
        return (
            <EmptyState
                header={t("experienceView.tabs.nothingAvailable")}
                description={t("experienceView.tabs.emptyAll")}
            />
        );
    }

    const hasRemainingItems = experiencesNotBookedYet.length > 0 || sectionsNotBookedYet.length > 0;

    return (
        <Stack spacing={ThemeSpaceVariable.None}>
            {hasBookedItems && hasRemainingItems && <NudgeHeader fullWidth />}
            <ExperienceGrid
                experiences={experiencesNotBookedYet}
                layouts={experienceLayouts}
                availability={experienceAvailability}
                onExperienceClick={onExperienceClick}
            />
            <SectionList
                sections={sectionsNotBookedYet}
                availability={sectionAvailability}
                divider={experiencesNotBookedYet.length > 0}
                onSectionClick={onSectionClick}
            />
            <BookedEverythingMessage
                isActivityBooking={true}
                sectionsNotBookedYet={sectionsNotBookedYet}
                experiencesNotBookedYet={experiencesNotBookedYet}
                hasBookedItems={hasBookedItems}
            />
        </Stack>
    );
}

interface ActivityTabsViewProps {
    state: ExperienceViewState;
    onExperienceClick: (experience: ExperienceViewExperienceItem) => void;
    onSectionClick: (section: ExperienceViewSectionItem) => void;
}

const tabFontSizeResponsive = {
    base: `${ThemeFontSizeVariable.Medium} !important`,
    md: `${ThemeFontSizeVariable.Large} !important`,
    lg: `${ThemeFontSizeVariable.XLarge} !important`,
};

/**
 * Experience view content for activity bookings that offer BOTH experiences
 * and sections — renders three filter tabs (Show All / Experiences / Activities).
 */
function ActivityTabsView({ state, onExperienceClick, onSectionClick }: ActivityTabsViewProps) {
    const { t } = useTranslation();
    const {
        experiences,
        experiencesNotBookedYet,
        experienceLayouts,
        experienceAvailability,
        sectionAvailability,
        sections,
        sectionsNotBookedYet,
        hasBookedItems,
    } = state;

    const hasRemainingItems = experiencesNotBookedYet.length > 0 || sectionsNotBookedYet.length > 0;

    const bookedEverything = (
        <BookedEverythingMessage
            isActivityBooking={true}
            sectionsNotBookedYet={sectionsNotBookedYet}
            experiencesNotBookedYet={experiencesNotBookedYet}
            hasBookedItems={hasBookedItems}
        />
    );

    const experienceGrid = (renderOnlyExperience: boolean = false) => (
        <ExperienceGrid
            experiences={experiencesNotBookedYet}
            layouts={experienceLayouts}
            availability={experienceAvailability}
            renderOnlyExperience={renderOnlyExperience}
            onExperienceClick={onExperienceClick}
        />
    );

    const sectionList = (divider: boolean = false) => (
        <SectionList
            sections={sectionsNotBookedYet}
            availability={sectionAvailability}
            divider={divider}
            onSectionClick={onSectionClick}
        />
    );

    const tabConfig = [
        {
            label: t("experienceView.tabs.showAll"),
            isEmpty: sections.length === 0 && experiences.length === 0,
            emptyDescription: t("experienceView.tabs.emptyAll"),
            content: (
                <Stack spacing={ThemeSpaceVariable.None}>
                    {hasBookedItems && hasRemainingItems && <NudgeHeader fullWidth />}
                    {experienceGrid()}
                    {sectionList(experiencesNotBookedYet.length > 0)}
                    {bookedEverything}
                </Stack>
            ),
        },
        {
            label: t("experienceView.tabs.experiences"),
            isEmpty: experiences.length === 0,
            emptyDescription: t("experienceView.tabs.emptyExperiences"),
            content: (
                <>
                    {hasBookedItems && experiencesNotBookedYet.length > 0 && (
                        <NudgeHeader fullWidth />
                    )}
                    {experienceGrid(true)}
                    {bookedEverything}
                </>
            ),
        },
        {
            label: t("experienceView.tabs.activities"),
            isEmpty: sections.length === 0,
            emptyDescription: t("experienceView.tabs.emptyActivities"),
            content: (
                <>
                    {hasBookedItems && sectionsNotBookedYet.length > 0 && <NudgeHeader fullWidth />}
                    {sectionList()}
                    {bookedEverything}
                </>
            ),
        },
    ];

    return (
        <Tabs variant="line" p={0}>
            <TabList gap={ThemeSpaceVariable.Medium} mb={ThemeSpaceVariable.Medium}>
                {tabConfig.map((tab) => (
                    <Tab
                        key={tab.label}
                        maxW="unset"
                        padding="0 !important"
                        fontSize={tabFontSizeResponsive}
                    >
                        {tab.label}
                    </Tab>
                ))}
            </TabList>
            <TabPanels>
                {tabConfig.map((tab) => (
                    <TabPanel key={tab.label}>
                        {tab.isEmpty ? (
                            <EmptyState
                                header={t("experienceView.tabs.nothingAvailable")}
                                description={tab.emptyDescription}
                            />
                        ) : (
                            tab.content
                        )}
                    </TabPanel>
                ))}
            </TabPanels>
        </Tabs>
    );
}

interface ExperienceViewContentProps {
    state: ExperienceViewState;
    isActivityBooking: boolean;
    isLoadingAvailableTimes: boolean;
    onExperienceClick: (experience: ExperienceViewExperienceItem) => void;
    onSectionClick: (section: ExperienceViewSectionItem) => void;
    onWebRuleClick: (webRuleAsSection: AvailableTimesSection) => void;
}

/**
 * Picks the correct view mode based on booking type and available data:
 * skeleton while loading, tabs when activity booking has both types,
 * single list when activity booking has one type, simple booking otherwise.
 */
function ExperienceViewContent({
    state,
    isActivityBooking,
    isLoadingAvailableTimes,
    onExperienceClick,
    onSectionClick,
    onWebRuleClick,
}: ExperienceViewContentProps) {
    const { experiences, sections, webRules, hasBookedItems, showSkeleton } = state;
    const hasNoContent = isActivityBooking
        ? experiences.length === 0 && sections.length === 0
        : experiences.length === 0 && webRules.length === 0 && !hasBookedItems;
    const hasMultipleTypes = experiences.length > 0 && sections.length > 0;
    // Show skeleton while loading if we have nothing to display yet — avoids
    // flashing "Nothing Available" before the real data arrives.
    const shouldShowSkeleton = showSkeleton || (isLoadingAvailableTimes && hasNoContent);

    if (shouldShowSkeleton) return <ExperienceViewContentSkeleton />;

    if (isActivityBooking && hasMultipleTypes) {
        return (
            <ActivityTabsView
                state={state}
                onExperienceClick={onExperienceClick}
                onSectionClick={onSectionClick}
            />
        );
    }

    if (isActivityBooking) {
        return (
            <ActivitySingleView
                state={state}
                onExperienceClick={onExperienceClick}
                onSectionClick={onSectionClick}
            />
        );
    }

    return (
        <SimpleBookingView
            state={state}
            onExperienceClick={onExperienceClick}
            onWebRuleClick={onWebRuleClick}
        />
    );
}

export { ExperienceViewContent };
